// §11.13 / §9.1 — the Console as an in-process app.
//
// Reading the shell's own diagnostics is a grant, not a §4.1 capability — `logs` would let any
// app watch every other app's failures — so it arrives as a `ConsoleShellAccess` built at
// composition (D-059), exactly as the Inspector's bridge log does.
import { createRoot, type Root } from 'react-dom/client';
import type { ConsoleShellAccess, InProcessApp } from '@hangar/shared';
import { COPY } from '@/copy';
import { Console } from './Console';
import './console.css';

const roots = new WeakMap<HTMLElement, Root>();

export function createConsoleApp(access: ConsoleShellAccess): InProcessApp {
  return {
    id: 'console',
    name: 'Console',
    icon: { glyph: 'scroll-text', hue: 252 },
    keywords: ['log', 'logs', 'diagnostics', 'debug', 'errors', 'trace', 'events'],
    window: { width: 900, height: 560, minWidth: 560, minHeight: 320, multiWindow: true },
    // §9.2 — what the Windows taskbar offers on a right-click (D-100).
    jumpList: [{ id: 'new-window', label: COPY.console.newWindow }],

    onCommand(commandId, api) {
      if (commandId === 'new-window') void api.launch.app('console');
    },

    mount(container, api) {
      const root = createRoot(container);
      roots.set(container, root);
      root.render(<Console api={api} access={access} />);
    },

    unmount(container) {
      const root = roots.get(container);
      roots.delete(container);
      root?.unmount();
    },
  };
}
